import { byStart, optional, optionalInt64, type CalendarEntry, type CalendarSnapshot } from "./catalog.js";
import { boardViewHref, editionHref } from "./edition-links.js";
import type { BroadcastEntry } from "./gen/catalog_pb.js";

/**
 * The homepage's tournaments, derived from the calendar.
 *
 * The apex page is a grid of tournaments whose winner is not yet decided, over
 * the one calendar read the site already makes (`Catalog.ListBroadcasts`). Every
 * quantity a card shows — what the event is called, which round it opens, which
 * shelf it belongs on, whether it belongs on the page at all — is DERIVED here
 * and nowhere else: this module is pure (no DOM, no fetch, and no clock — `nowMs`
 * is injected, exactly as `hub.ts` takes it), so the rules are testable without a
 * browser and a card cannot change band halfway through a render.
 *
 * **This module parses nothing.** The owner's ruling (2026-09-15) is that the
 * Lichess parse lives in relay-control, which projects `tour_name`,
 * `tour_starts_at_ms` and `tour_ends_at_ms` onto `BroadcastEntry`; the site
 * groups and orders what the wire says. In particular no rule below reads
 * `title` other than to print it verbatim when the producer sent no tour name.
 */

/**
 * Which shelf a tour is on. A band, never "liveness computed from the clock".
 *
 * `ended` is the fourth shelf: the organiser's end date has passed and every
 * round is played, so the card is a door to the last round rather than a
 * countdown. It exists because `tour_ends_at_ms` is a DAY, and for the
 * Olympiad it is the start of the final round — see {@link cardFor}.
 */
export type TourBand = "live" | "in-progress" | "starting-soon" | "ended";

/**
 * One tour of an event, as the card's sections row shows it.
 *
 * A section is a whole per-tour card underneath ({@link tourCards} derives one
 * for every tour and keeps the ones that qualify); this is the part the event
 * card shows and links to.
 */
export interface TourSection {
  tourId: string;
  tourSlug: string;
  /** {@link sectionName}: what this tour's name adds to the group's. */
  name: string;
  band: TourBand;
  /** This section's own streaming rounds, earliest start first. */
  live: CalendarEntry[];
  /** The round this section opens. */
  target: CalendarEntry;
  href: string;
}

/** One pill of an event card's sections row: a section, its Lichess shards folded together. */
export interface SectionPill {
  name: string;
  href: string;
  band: TourBand;
}

/**
 * The sections row an event card shows (design review 2026-09-23; owner 2026-09-23: user-facing
 * names are SECTION-level, never the Lichess shard). The Olympiad's nine tours — "Open | Matches
 * 1-12" … "Women | Matches 76+" — fold by their first " | " segment into "Open" and "Women"; a
 * section with no shard suffix stays as it is. A pill opens its section's first live shard (else
 * its first), and takes the section's best band. Order is the card's own.
 */
export function foldSections(sections: readonly TourSection[]): SectionPill[] {
  const pills = new Map<string, { pill: SectionPill; live: boolean }>();
  for (const section of sections) {
    const name = section.name.split(" | ")[0]?.trim() || section.name;
    const live = section.band === "live";
    const found = pills.get(name);
    if (found === undefined) { pills.set(name, { pill: { name, href: section.href, band: section.band }, live }); continue; }
    if (live && !found.live) { found.pill.href = section.href; found.live = true; }
    if (bandRank(section.band) < bandRank(found.pill.band)) found.pill.band = section.band;
  }
  return [...pills.values()].map((entry) => entry.pill);
}

/**
 * One EVENT as the homepage grid shows it — a Lichess broadcast group when the
 * wire names one, else a single tour.
 *
 * The Olympiad is nine Lichess tours of one group, and nine cards for it pushed
 * everything else off the page. Every per-tour rule is unchanged and runs per
 * SECTION (`cardFor`); the fields below are that derivation AGGREGATED over the
 * sections, so a one-section event is exactly the card PR #2452 rendered.
 */
export interface TourCard {
  /** The group this card is, or the tour id when the wire names no group. */
  eventId: string;
  /** The Lichess group name, verbatim, or null when the tour is in no group. */
  groupName: string | null;
  /** The event's tours, in tier-then-name order. Never empty. */
  sections: TourSection[];
  /** The {@link target}'s tour. */
  tourId: string;
  tourSlug: string;
  /** {@link tourName} of the card's source round. */
  name: string;
  /** The cover image, byte-for-byte as served (it is signed), or null. */
  imageUrl: string | null;
  /** `"T1".."T4"`, or null when the entry carries none. */
  tier: string | null;
  timeControl: "Classical" | "Rapid" | "Blitz" | null;
  /** Lichess's notable-participants line, verbatim, or null. */
  players: string | null;
  tourStartsAtMs: number | null;
  tourEndsAtMs: number | null;
  band: TourBand;
  /** Streaming now, earliest start first. */
  live: CalendarEntry[];
  /** Scheduled rounds whose hour has passed inside the window, earliest first. */
  underway: CalendarEntry[];
  /** The next round to start, or null. */
  next: CalendarEntry | null;
  /** The most recently started finished round, or null. */
  lastFinished: CalendarEntry | null;
  /** The round the card opens. */
  target: CalendarEntry;
  href: string;
  boardCount: number | null;
}

export interface TourShelves {
  live: TourCard[];
  inProgress: TourCard[];
  startingSoon: TourCard[];
  /** Every round played and the organiser's date passed; opens the last round. Newest finish first. */
  ended: TourCard[];
}

/**
 * What the homepage should render.
 *
 * mirror-of: `shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/HomeSchedulePresentation.kt`
 * (`HomeScheduleStrip`) — a RESOLVED board may be empty, and that is a real
 * answer ("nothing is on"), never an invitation to substitute placeholder
 * content; only an unreachable/unfed calendar licenses one. The web's signal for
 * the second is the server's own `overlay_seeded`, not a caught exception, so a
 * seeded-but-quiet calendar can never be mistaken for a broken one. Agreement
 * test: `src/tours.test.ts` (`describe("tourBoard")`).
 */
export type TourBoard = { state: "unavailable" } | { state: "resolved"; shelves: TourShelves; total: number };

/**
 * How far ahead a tour with nothing under way is still worth a card.
 *
 * A week is the span in which "there is a tournament coming" is news a visitor
 * can act on; past it the grid fills with events whose first move is a fortnight
 * away and the page stops being about what is on.
 */
export const TOUR_HORIZON_MS = 7 * 86_400_000;

/**
 * How long after its announced start a round that we are not streaming still
 * counts as under way.
 *
 * A round begins on the ORGANISER's clock, and our overlay learns it is active
 * only once a board moves — a gap that is minutes on a good day and hours when
 * the relay is behind. A day is the same window `FINISHED_WINDOW_SECS` keeps
 * finished rounds for, so the two ends of "today's play" agree. Older overdue
 * rounds are ignored rather than trusted: a round three days past its hour that
 * never went live is a scheduling artefact, not a game.
 */
export const UNDERWAY_WINDOW_MS = 86_400_000;

export interface TourCardOptions {
  horizonMs?: number;
  underwayWindowMs?: number;
}

/**
 * What to call a tournament.
 *
 * `tour_name` is Lichess's `tour.name`, projected by relay-control — the event's
 * name without the round suffix `title` carries. The `title` fallback is a
 * DISPLAY fallback for a producer that predates the field (it is printed
 * verbatim, round suffix and all); deriving the event name from it here would be
 * the parse the owner placed in relay-control. The slug is the last resort: a
 * card with no name at all is worse than a card labelled by its URL.
 */
export function tourName(entry: BroadcastEntry): string {
  const named = optional(entry.tourName);
  if (named !== null) return named;
  if (optional(entry.title) !== null) return entry.title;
  return entry.tourSlug;
}

/**
 * The key the homepage groups tours into one card by.
 *
 * `group_id` is relay-control's projection of the Lichess broadcast group
 * (#2459, spec §10) — the producer's own statement that nine Olympiad tours are
 * one event. Empty means "in no group", which is a statement and not a fault:
 * the tour is then its own event, which is what every card was before the field
 * existed and what the site still renders until the backends serve it.
 */
export function eventKey(entry: BroadcastEntry): string {
  return optional(entry.groupId) ?? entry.tourId;
}

/**
 * What a section is labelled by: what the tour's name ADDS to the group's.
 *
 * mirror-of: lila's `shortTourName` — "46th Chess Olympiad Samarkand 2026 |
 * Open | I" under its own group reads "Open | I". The prefix test is exact and
 * the name is otherwise verbatim: this is not a parse of the tour name (that
 * lives in relay-control), it is a subtraction of a string the wire sent
 * alongside it, so a group whose name is not a prefix relabels nothing. A tour
 * whose name IS the group name keeps its name — a blank section label is worse
 * than a repeated one.
 */
export function sectionName(tourName: string, groupName: string | null): string {
  const group = (groupName ?? "").trim();
  if (group.length === 0 || !tourName.startsWith(group)) return tourName;
  const rest = tourName.slice(group.length).replace(/^[^\p{L}\p{N}]+/u, "").trim();
  return rest.length === 0 ? tourName : rest;
}

/** The separator between an event and its section in {@link sectionLevelHeading}. */
export const SECTION_SEPARATOR = " · ";

/**
 * "<event> · <section>": the one way a section-level event name is written — the watch page's
 * title (`live/event-standings.ts` `sectionHeading`) and {@link sectionLevelName}.
 */
export function sectionLevelHeading(group: string, section: string): string {
  return [group, section].filter((part) => part.length > 0).join(SECTION_SEPARATOR);
}

/**
 * {@link sectionLevelHeading} read back: the event and its section, so the watch page can set the
 * section as a muted suffix of the title (fix 23). Splits at the LAST separator; a line without one
 * is all event.
 */
export function splitSectionLevelHeading(line: string): { event: string; section: string } {
  const at = line.lastIndexOf(SECTION_SEPARATOR);
  return at === -1 ? { event: line, section: "" } : { event: line.slice(0, at), section: line.slice(at + SECTION_SEPARATOR.length) };
}

/**
 * The watch page's section-level event name for a page that has only a Lichess tour name — a
 * player's notable games and recent events (audit 2026-09-25, fix 7): "Norway Chess 2026 | Open"
 * → "Norway Chess 2026 · Open", "… 2026 | Open | Matches 1-12" → "… 2026 · Open". The group is
 * the name's first " | " segment (lila's group prefix, which {@link sectionName} subtracts); the
 * section is the first segment of the rest ({@link foldSections}' rule), never the shard after
 * it. A name with no " | " is already section level and is returned as it is. Agrees with
 * `sectionHeading` wherever the calendar can place the edition (event-standings.test.ts).
 */
export function sectionLevelName(tourName: string): string {
  const group = tourName.split(" | ")[0]?.trim() ?? "";
  if (group.length === tourName.trim().length) return tourName;
  return sectionLevelHeading(group, sectionName(tourName, group).split(" | ")[0]?.trim() ?? "");
}

/**
 * The time-control label, from Lichess's own `fide_tc`.
 *
 * mirror-of: `shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/ScheduleViewModel.kt:72-77`
 * (`classifyBroadcast`) — the app's mapping, and the reason it is a mapping and
 * not a heuristic: Lichess labels classical games "standard", so a
 * title-substring guess classified ~60% of real events as UNKNOWN. Unrecognised
 * or absent stays null; it is never guessed. Agreement test:
 * `src/tours.test.ts` (`describe("timeControlLabel")`).
 */
export function timeControlLabel(fideTc: string | undefined | null): TourCard["timeControl"] {
  switch ((fideTc ?? "").toLowerCase()) {
    case "standard":
      return "Classical";
    case "rapid":
      return "Rapid";
    case "blitz":
      return "Blitz";
    default:
      return null;
  }
}

/**
 * Newest first, with undated rounds LAST.
 *
 * Derived from `byStart` rather than written out again: plainly reversing that
 * comparator would sort an undated round first, and `lastFinished` would then be
 * a round nobody can date.
 */
const newestFirst = (a: CalendarEntry, b: CalendarEntry): number =>
  a.startsAtMs === null || b.startsAtMs === null ? byStart(a, b) : -byStart(a, b);

/** `T1` < `T2` < `T3` < `T4` < anything else. Unknown sorts last, never first. */
const tierRank = (tier: string | null): number => {
  const rank = tier === null ? Number.NaN : Number.parseInt(tier.replace(/^T/iu, ""), 10);
  return Number.isInteger(rank) && rank >= 1 && rank <= 4 ? rank : 5;
};

/**
 * The clock a shelf is read by: when the live play started, else when the next
 * round does. An ended shelf reads the other way — the most recently finished
 * event first — which is why its clock is the negated last start.
 */
const bandClock = (card: TourCard): number | null => {
  if (card.band === "live") return card.live[0]?.startsAtMs ?? null;
  if (card.band === "ended") {
    const last = card.lastFinished?.startsAtMs ?? null;
    return last === null ? null : -last;
  }
  return card.next?.startsAtMs ?? null;
};

const byShelfOrder = (a: TourCard, b: TourCard): number => {
  const byTier = tierRank(a.tier) - tierRank(b.tier);
  if (byTier !== 0) return byTier;
  const clockA = bandClock(a) ?? Number.MAX_SAFE_INTEGER;
  const clockB = bandClock(b) ?? Number.MAX_SAFE_INTEGER;
  if (clockA !== clockB) return clockA - clockB;
  return a.tourId < b.tourId ? -1 : a.tourId > b.tourId ? 1 : 0;
};

/**
 * The tournaments on the calendar, banded and ordered.
 *
 * @param nowMs caller-pinned wall clock, so every card in one render is banded
 *   against the same instant — the reason `HomeSchedulePresentation.strip` takes
 *   it too.
 */
export function tourCards(
  calendar: readonly CalendarEntry[],
  nowMs: number,
  opts: TourCardOptions = {},
): TourShelves {
  const horizonMs = opts.horizonMs ?? TOUR_HORIZON_MS;
  const underwayWindowMs = opts.underwayWindowMs ?? UNDERWAY_WINDOW_MS;

  // Two levels, both in insertion order, so an input order is the only tiebreak
  // an image fallback or an undated round ever sees: event (the served group,
  // else the tour) -> tour -> rounds.
  const events = new Map<string, Map<string, CalendarEntry[]>>();
  for (const item of calendar) {
    const key = eventKey(item.entry);
    let tours = events.get(key);
    if (tours === undefined) {
      tours = new Map<string, CalendarEntry[]>();
      events.set(key, tours);
    }
    const rows = tours.get(item.entry.tourId);
    if (rows) rows.push(item);
    else tours.set(item.entry.tourId, [item]);
  }

  const shelves: TourShelves = { live: [], inProgress: [], startingSoon: [], ended: [] };
  for (const [eventId, tours] of events) {
    const sections: TourCard[] = [];
    for (const [tourId, rows] of tours) {
      const card = cardFor(tourId, rows, nowMs, horizonMs, underwayWindowMs);
      if (card !== null) sections.push(card);
    }
    const card = eventCard(eventId, sections, tours);
    if (card === null) continue;
    if (card.band === "live") shelves.live.push(card);
    else if (card.band === "in-progress") shelves.inProgress.push(card);
    else if (card.band === "ended") shelves.ended.push(card);
    else shelves.startingSoon.push(card);
  }

  shelves.live.sort(byShelfOrder);
  shelves.inProgress.sort(byShelfOrder);
  shelves.startingSoon.sort(byShelfOrder);
  shelves.ended.sort(byShelfOrder);
  return shelves;
}

/** The latest dated start among a tour's rounds, whatever their state, or null when none is dated. */
const lastStart = (rows: readonly CalendarEntry[]): number | null =>
  rows.reduce<number | null>((latest, c) => (c.startsAtMs === null ? latest : latest === null ? c.startsAtMs : Math.max(latest, c.startsAtMs)), null);

/** One tour's card, or null when the tour does not belong on the page. */
function cardFor(
  tourId: string,
  rows: readonly CalendarEntry[],
  nowMs: number,
  horizonMs: number,
  underwayWindowMs: number,
): TourCard | null {
  const live = rows.filter((c) => c.state === "active").sort(byStart);
  const finished = rows.filter((c) => c.state === "finished").sort(newestFirst);
  const scheduled = rows.filter((c) => c.state === "upcoming");

  const ahead = scheduled.filter((c) => c.startsAtMs !== null && c.startsAtMs >= nowMs).sort(byStart);
  // An undated round is "starts after the previous one", which is only a
  // statement about a tour that HAS a previous one.
  const undated = scheduled.find((c) => c.startsAtMs === null) ?? null;
  const next = ahead[0] ?? (live.length > 0 || finished.length > 0 ? undated : null) ?? null;

  const underway = scheduled
    .filter((c) => c.startsAtMs !== null && c.startsAtMs < nowMs && nowMs - c.startsAtMs <= underwayWindowMs)
    .sort(byStart);

  const firstRow = rows[0];
  if (firstRow === undefined) return null;
  const tourStartsAtMs = optionalInt64(firstRow.entry.tourStartsAtMs);
  const tourEndsAtMs = optionalInt64(firstRow.entry.tourEndsAtMs);

  // `tour_ends_at_ms` is Lichess's `tour.dates[1]` — a day, projected as an
  // instant, and for the Olympiad that instant is the START of the final round
  // (06:15 UTC, 27 Sep 2026 = Round 11's start; measured 2026-09-22). So "the
  // organiser says it is over" is read against the rounds, not the clock alone:
  //
  //  * A board still streaming outranks the date — the wire beats the calendar
  //    entry — and so does a round whose hour has passed inside the underway
  //    window, and a dated round still ahead. None of those is "over".
  //  * Only when every round is played does the passed date end the tour —
  //    and "every round" is read off `next`, the same value the running card
  //    shows: nothing live, nothing under way, and no round still to come,
  //    dated or UNDATED (an announced tiebreak awaiting an hour is a round
  //    nobody has played, and the card stays in progress pointing at it).
  //    Then the card STAYS, as an `ended` door to the last round, for as long
  //    as the calendar carries that round. It used to vanish, with no recap
  //    path.
  //  * A tour past its date with no round to open (a scheduled round days
  //    overdue that never went live) is a scheduling artefact, and is dropped.
  const organiserSaysOver = tourEndsAtMs !== null && tourEndsAtMs < nowMs;
  const everyRoundPlayed = live.length === 0 && underway.length === 0 && next === null;
  if (organiserSaysOver && everyRoundPlayed) {
    const last = finished[0];
    if (last === undefined) return null;
    return tourCard(tourId, rows, tourStartsAtMs, tourEndsAtMs, {
      band: "ended",
      live,
      underway: [],
      next: null,
      lastFinished: last,
      target: last,
    });
  }

  // The tour is within its dates until the LATER of the organiser's end and
  // the last round's start plus the underway window: a date that names the
  // final round's start must not close the event before that round is done.
  const latestStart = lastStart(rows);
  const tourClosesAtMs = tourEndsAtMs === null ? null : latestStart === null ? tourEndsAtMs : Math.max(tourEndsAtMs, latestStart + underwayWindowMs);

  const datedSoon = next?.startsAtMs !== null && next?.startsAtMs !== undefined && next.startsAtMs - nowMs <= horizonMs;
  // The last arm is what the served tour dates buy: a tour on a rest day, with
  // nothing live, nothing finished in the window and its next round beyond the
  // horizon, is still under way and still worth a card.
  const withinTourDates =
    tourStartsAtMs !== null && tourStartsAtMs <= nowMs && (tourClosesAtMs === null || tourClosesAtMs >= nowMs) && next !== null;
  const qualifies = live.length > 0 || finished.length > 0 || underway.length > 0 || datedSoon || withinTourDates;
  if (!qualifies) return null;

  // Every qualifying arm guarantees one of these four.
  const target = live[0] ?? underway[underway.length - 1] ?? next ?? finished[0];
  if (target === undefined) return null;

  const band: TourBand =
    live.length > 0
      ? "live"
      : finished.length > 0 || underway.length > 0 || (tourStartsAtMs !== null && tourStartsAtMs <= nowMs)
        ? "in-progress"
        : "starting-soon";

  return tourCard(tourId, rows, tourStartsAtMs, tourEndsAtMs, {
    band,
    live,
    underway,
    next,
    lastFinished: finished[0] ?? null,
    target,
  });
}

/**
 * The per-tour card's fields that are the SAME whatever band it is on: name,
 * art, tier, time control and players come from the round that best represents
 * the tour now, and the href from the target. Stated once so the ended card and
 * the running card cannot drift apart in what they say about the tournament.
 */
function tourCard(
  tourId: string,
  rows: readonly CalendarEntry[],
  tourStartsAtMs: number | null,
  tourEndsAtMs: number | null,
  derived: Pick<TourCard, "band" | "live" | "underway" | "next" | "lastFinished" | "target">,
): TourCard {
  const { target } = derived;
  // The tour's own fields come from the round that best represents it now.
  const source = derived.live[0] ?? derived.next ?? derived.lastFinished ?? target;
  return {
    // A per-tour card IS a one-section event until `eventCard` aggregates it.
    eventId: tourId,
    groupName: null,
    sections: [],
    tourId,
    tourSlug: source.entry.tourSlug,
    name: tourName(source.entry),
    imageUrl: optional(source.entry.imageUrl) ?? rows.map((c) => optional(c.entry.imageUrl)).find((u) => u !== null) ?? null,
    tier: optional(source.entry.tier),
    timeControl: timeControlLabel(source.entry.fideTc),
    players: optional(source.entry.players),
    tourStartsAtMs,
    tourEndsAtMs,
    ...derived,
    href: tournamentHref(target),
    boardCount: target.entry.boardCount ?? null,
  };
}

/** live beats in-progress beats starting-soon beats ended. */
const bandRank = (band: TourBand): number => (band === "live" ? 0 : band === "in-progress" ? 1 : band === "starting-soon" ? 2 : 3);

/**
 * The event's card, aggregated from the cards of its tours.
 *
 * Every quantity below is the SAME rule the per-tour derivation applies, read
 * over the union of the sections — which is why a one-section event comes out
 * byte-identical to `cardFor`'s answer and a calendar with no group on it
 * renders exactly the shelves PR #2452 did. Null when no tour of the event
 * qualifies: totality holds per EVENT, as it held per tour.
 */
function eventCard(eventId: string, sections: readonly TourCard[], tours: ReadonlyMap<string, CalendarEntry[]>): TourCard | null {
  if (sections.length === 0) return null;

  // The group name is the producer's, taken from the first row that carries one
  // — every tour of a group carries it, but a section may have aged off the
  // calendar between them.
  const groupName =
    [...tours.values()]
      .flat()
      .map((c) => optional(c.entry.groupName))
      .find((n) => n !== null) ?? null;

  const ordered = [...sections].sort((a, b) => {
    const byTier = tierRank(a.tier) - tierRank(b.tier);
    if (byTier !== 0) return byTier;
    const nameA = sectionName(a.name, groupName);
    const nameB = sectionName(b.name, groupName);
    if (nameA !== nameB) return nameA < nameB ? -1 : 1;
    return a.tourId < b.tourId ? -1 : a.tourId > b.tourId ? 1 : 0;
  });

  const live = ordered.flatMap((s) => s.live).sort(byStart);
  const underway = ordered.flatMap((s) => s.underway).sort(byStart);
  const finished = ordered.flatMap((s) => (s.lastFinished === null ? [] : [s.lastFinished])).sort(newestFirst);

  // The undated rule is the sections' own: a dated round ahead wins, and an
  // undated "after the previous one" is kept only because a section already
  // decided it was meaningful.
  const nexts = ordered.flatMap((s) => (s.next === null ? [] : [s.next]));
  const next = nexts.filter((c) => c.startsAtMs !== null).sort(byStart)[0] ?? nexts[0] ?? null;

  // The same four-way rule `cardFor` applies, over the union.
  const target = live[0] ?? underway[underway.length - 1] ?? next ?? finished[0];
  if (target === undefined) return null;

  // The section the card's own round belongs to: what the card says about time
  // control and slug is said about the tournament it opens.
  const targetSection = ordered.find((s) => s.tourId === target.entry.tourId) ?? ordered[0];
  if (targetSection === undefined) return null;

  const best = [...ordered].sort((a, b) => bandRank(a.band) - bandRank(b.band))[0] ?? targetSection;
  const starts = ordered.flatMap((s) => (s.tourStartsAtMs === null ? [] : [s.tourStartsAtMs]));
  const ends = ordered.flatMap((s) => (s.tourEndsAtMs === null ? [] : [s.tourEndsAtMs]));

  return {
    eventId,
    groupName,
    sections: ordered.map((s) => ({
      tourId: s.tourId,
      tourSlug: s.tourSlug,
      name: sectionName(s.name, groupName),
      band: s.band,
      live: s.live,
      target: s.target,
      href: s.href,
    })),
    tourId: targetSection.tourId,
    tourSlug: targetSection.tourSlug,
    name: groupName ?? targetSection.name,
    imageUrl: ordered.map((s) => s.imageUrl).find((u) => u !== null) ?? null,
    tier: [...ordered].sort((a, b) => tierRank(a.tier) - tierRank(b.tier))[0]?.tier ?? null,
    timeControl: targetSection.timeControl,
    players: ordered.map((s) => s.players).find((p) => p !== null) ?? null,
    tourStartsAtMs: starts.length === 0 ? null : Math.min(...starts),
    tourEndsAtMs: ends.length === 0 ? null : Math.max(...ends),
    band: best.band,
    live,
    underway,
    next,
    lastFinished: finished[0] ?? null,
    target,
    href: tournamentHref(target),
    boardCount: target.entry.boardCount ?? null,
  };
}

/**
 * Where "open this round" points: the round's viewer, the board layer of its
 * tournament page (`edition-links.ts` `boardViewHref`, O5 — round pages are
 * retired). The Rounds rail, the live-round chips and a merged round's
 * finished-section links all open the viewer on that round.
 *
 * A round with no edition id (a calendar row the controller could not mint one
 * for) has no tournament page to sit on, so it keeps the legacy `/round/<id>`
 * address, whose route still renders the viewer when no edition resolves.
 */
export const roundHref = (target: CalendarEntry): string => target.editionId
  ? boardViewHref(target.editionId, target.entry.roundId, null)
  : `/round/${encodeURIComponent(target.entry.roundId)}`;

/** Tournament cards address the edition itself; "open this round" is {@link roundHref}. */
export const tournamentHref = (target: CalendarEntry): string => target.editionId
  ? editionHref(target.editionId)
  : roundHref(target);

/**
 * The homepage's whole answer, from one calendar snapshot.
 *
 * `overlay_seeded === false` is "the calendar has never been fed", which is not
 * the same statement as "nothing is on" and must not render as one.
 */
export function tourBoard(snapshot: CalendarSnapshot, nowMs: number, opts: TourCardOptions = {}): TourBoard {
  if (!snapshot.overlaySeeded) return { state: "unavailable" };
  const shelves = tourCards(snapshot.entries, nowMs, opts);
  return {
    state: "resolved",
    shelves,
    total: shelves.live.length + shelves.inProgress.length + shelves.startingSoon.length + shelves.ended.length,
  };
}
